import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";

// Эрхийн цорын ганц эх сурвалж:
// parkings/{Firebase Auth displayName}/users/{Firebase Auth email}
// users/{uid} collection-оос role, permissions огт уншихгүй.
export const AuthContext = createContext(null);

const EMPTY = {
  user: null,
  role: null,
  permissions: [],
  tenantId: null,
  profile: null,
  loading: true,
  error: null,
  profileMissing: false,
};

const normalizedRole = (value) => String(value ?? "").trim().toLowerCase();
const normalizedStatus = (value) => String(value ?? "active").trim().toLowerCase();
const normalizedPermission = (value) => {
  const str = String(value ?? "").trim();
  if (!str || str === "*") return str;
  return (str.startsWith("/") ? str : `/${str}`)
    .replace(/\/+$/, "")
    .toLowerCase();
};

function readProfile(raw) {
  const data = raw && typeof raw === "object" ? raw : {};
  // Хуучин nested role/permissions бүтэцтэй баримтыг давхар дэмжинэ.
  const nested = Object.values(data).find(
    (value) => value && typeof value === "object" && !Array.isArray(value) && value.role
  ) || {};
  const role = normalizedRole(data.role || nested.role);
  const permissions = Array.isArray(data.permissions)
    ? data.permissions
    : Array.isArray(nested.permissions)
      ? nested.permissions
      : [];
  const status = normalizedStatus(data.status ?? nested.status);
  const disabled = data.disabled === true || nested.disabled === true;
  const active = data.active !== false && nested.active !== false &&
    data.isActive !== false && nested.isActive !== false;

  return {
    role: role || null,
    permissions: [...new Set(permissions
      .filter((item) => typeof item === "string")
      .map((item) => item.trim())
      .filter(Boolean))],
    status,
    enabled: !disabled && active && status === "active",
  };
}

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();
    let sequence = 0;
    let unsubscribeProfile = null;

    const stopProfile = () => {
      if (typeof unsubscribeProfile === "function") unsubscribeProfile();
      unsubscribeProfile = null;
    };

    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      const currentSequence = ++sequence;
      stopProfile();

      if (!currentUser) {
        setState({ ...EMPTY, loading: false });
        return;
      }

      // Өмнөх хэрэглэгчийн эрхийг шинэ нэвтрэлтэд түр ашиглахгүй.
      setState({ ...EMPTY, user: currentUser, loading: true });
      const isCurrent = () => sequence === currentSequence;
      const parkingId = String(currentUser.displayName || "").trim();
      const email = String(currentUser.email || "").trim();

      if (!parkingId || !email || parkingId.includes("/") || email.includes("/")) {
        setState({ ...EMPTY, user: currentUser, loading: false, profileMissing: true });
        console.warn("AuthProvider: Firebase user.displayName (parkingId) эсвэл email байхгүй/буруу байна.");
        return;
      }

      const userRef = doc(db, "parkings", parkingId, "users", email);
      unsubscribeProfile = onSnapshot(
        userRef,
        (snapshot) => {
          if (!isCurrent()) return;
          if (!snapshot.exists()) {
            setState({ ...EMPTY, user: currentUser, tenantId: parkingId, loading: false, profileMissing: true });
            return;
          }
          const raw = snapshot.data() || {};
          const fields = readProfile(raw);
          setState({
            user: currentUser,
            role: fields.role,
            permissions: fields.permissions,
            tenantId: parkingId,
            profile: { ...raw, source: "parking-user" },
            loading: false,
            error: null,
            profileMissing: false,
          });
        },
        (error) => {
          if (!isCurrent()) return;
          console.error("AuthProvider: parkings/{parkingId}/users/{email} эрх уншихад алдаа:", error);
          setState({ ...EMPTY, user: currentUser, tenantId: parkingId, loading: false, error });
        }
      );
    });

    return () => {
      ++sequence;
      stopProfile();
      unsubscribeAuth();
    };
  }, []);

  const fields = readProfile(state.profile);
  const isActive = Boolean(
    state.user && !state.loading && !state.error &&
    !state.profileMissing && state.profile && fields.role && fields.enabled
  );
  const logout = useCallback(() => signOut(getAuth()), []);
  const hasRole = useCallback((...roles) =>
    isActive && roles.flat().map(normalizedRole).includes(state.role),
  [isActive, state.role]);
  const hasPermission = useCallback((permission) => {
    if (!isActive) return false;
    if (state.role === "superadmin" || state.role === "master") return true;
    const allowed = new Set(state.permissions.map(normalizedPermission));
    return allowed.has("*") || allowed.has(normalizedPermission(permission));
  }, [isActive, state.role, state.permissions]);

  const value = useMemo(() => ({
    ...state,
    isActive,
    isAuthenticated: Boolean(state.user),
    logout,
    hasRole,
    hasPermission,
  }), [state, isActive, logout, hasRole, hasPermission]);

  // Router-ийг auth loading үед unmount хийхгүй.
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);